import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';

import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';

import { type JobItemDetails, useJobItemDetails } from '../../api/use-job-item-details';
import { getWorkflowStatus, type WorkflowStatusType } from '../../hooks/use-job-profile-workflow-status';
import { ArchivedNotification } from './ArchivedNotification';
import { AssignReviewerNotification } from './AssignReviewerNotification';
import { GeneratingNotification } from './GeneratingNotification';
import { PublisherNotification } from './PublisherNotification';
import { ReviewerNotification } from './ReviewerNotification';

type ItemType = 'job-level' | 'job-role';
export type NotificationBarProps = {
    readonly architectureId: string;
    readonly isArchived?: boolean;
    readonly itemId: string;
    readonly itemType: ItemType;
    readonly onDeleteComplete?: () => void;
    readonly onRestoreComplete?: () => void;
    readonly version: string | undefined;
};

const renderNotification = (
    workflowStatus: WorkflowStatusType,
    architectureId: string,
    item: JobItemDetails,
    itemType: 'job-level' | 'job-role',
): ReactNode => {
    switch (workflowStatus) {
        case 'is-generating':
            return <GeneratingNotification item={item} />;
        case 'assign-reviewer':
            return <AssignReviewerNotification architectureId={architectureId} item={item} itemType={itemType} />;
        case 'review':
            return <ReviewerNotification architectureId={architectureId} item={item} itemType={itemType} />;
        case 'publish':
            return <PublisherNotification item={item} />;
        // biome-ignore lint/complexity/noUselessSwitchCase: suppressed due to migration
        case 'none':
        default:
            return null;
    }
};

export const NotificationBar: FunctionComponent<NotificationBarProps> = ({
    architectureId,
    isArchived,
    itemId,
    itemType,
    onDeleteComplete,
    onRestoreComplete,
    version,
}) => {
    const { data: item, error, isLoading } = useJobItemDetails({ architectureId, itemId, itemType, version });

    if (isLoading) {
        return <SkeletonBase style={{ width: '200px' }} />;
    }

    if (!is.nullOrUndefined(error) || is.nullOrUndefined(item?.parentId)) {
        return null;
    }

    if (isArchived === true) {
        return (
            <ArchivedNotification
                architectureId={architectureId}
                item={item}
                itemType={itemType}
                onDeleteComplete={onDeleteComplete}
                onRestoreComplete={onRestoreComplete}
            />
        );
    }

    const workflowStatus = getWorkflowStatus(item);

    return renderNotification(workflowStatus, architectureId, item, itemType);
};
